/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { DownloadCsvEndpoint, AuthorizationHeader } from "constants/endpoints"
import { useCallback, useEffect, useRef, useState } from "react"
import { store } from "store/store"
import {
  GetCsvReportStatusResponse,
  RequestCsvReportResponse,
} from "types/api/menuFeedback.type"
import { mexInsightsHttpRequest } from "utils/network"
import { storageHelper } from "utils/storage"
import axios, { CancelToken, CancelTokenSource } from "axios"
import { saveFile } from "utils/misc"
import dayjs from "dayjs"
import { filterOutlets } from "../FeedbackFilters/utils"

export enum TaskStatus {
  PENDING = "PENDING",
  DOWNLOADING = "DOWNLOADING",
  DONE = "DONE",
  ERROR = "ERROR",
}

export enum DownloaderStatus {
  IDLE = "IDLE",
  DOWNLOADING = "DOWNLOADING",
  DONE = "DONE",
}

export default function useBulkDownload({
  cities,
  onDone,
}: {
  cities: string[]
  onDone?: () => void
}) {
  const [cityQueue, setCityQueue] = useState<
    {
      city: string
      status: TaskStatus
    }[]
  >([])
  const [status, setStatus] = useState<DownloaderStatus>(DownloaderStatus.IDLE)
  const cancelTokensRef = useRef<CancelTokenSource[]>([])
  const isDownloading = useRef<boolean>(false)

  const download = useCallback(
    async (index: number) => {
      if (!isDownloading.current) {
        return
      }

      if (index >= cityQueue.length) {
        setStatus(DownloaderStatus.DONE)
        // put error tasks at the top
        setCityQueue(q => [
          ...q.sort((a, b) => (b.status === TaskStatus.ERROR ? 1 : -1)),
        ])
        onDone?.()
        return
      }
      setCityQueue(prev => {
        const next = [...prev]
        next[index].status = TaskStatus.DOWNLOADING
        return next
      })

      const cancelToken = axios.CancelToken.source()
      cancelTokensRef.current.push(cancelToken)

      const loadReviewResult = await loadReviewCSV(
        cityQueue[index].city,
        cancelToken.token,
      )

      if (!isDownloading.current) {
        // Do not use `status` as reference here since the value wouldn't be updated
        return
      }

      setCityQueue(prev => {
        const next = [...prev]
        next[index].status = loadReviewResult
          ? TaskStatus.DONE
          : TaskStatus.ERROR
        return next
      })

      // recursively download next
      download(index + 1)
    },
    [onDone, cityQueue, setCityQueue],
  )

  useEffect(() => {
    if (
      !isDownloading.current &&
      status === DownloaderStatus.DOWNLOADING &&
      cityQueue.length > 0
    ) {
      // ensure we only trigger once
      isDownloading.current = true
      download(0)
    }
  }, [status, cityQueue, download, isDownloading])

  useEffect(() => {
    if (status !== DownloaderStatus.DOWNLOADING && isDownloading.current) {
      isDownloading.current = false
    }
  }, [status, isDownloading])

  const start = () => {
    setCityQueue(
      cities.map(city => ({
        city,
        status: TaskStatus.PENDING,
      })),
    )
    setStatus(DownloaderStatus.DOWNLOADING)
  }

  const cancel = () => {
    setStatus(DownloaderStatus.IDLE)
    setCityQueue([])
    cancelTokensRef.current.forEach(c => c.cancel())
    cancelTokensRef.current = []
  }

  return {
    cityQueue,
    start,
    status,
    cancel,
  }
}

// modified from /src/store/duck/feedback.ts
export const loadReviewCSV = async (city: string, cancelToken: CancelToken) => {
  const state = store.getState()
  const { filterState } = state.feedback

  if (!filterState) {
    return false
  }

  const filteredOutlets = filterOutlets({
    outlets: state.menu?.outlets || [],
    cityName: city,
    businessType: filterState.businessType,
  })

  const start = dayjs(filterState.startDate).format("DD_MM_YY")
  const end = dayjs(filterState.endDate).format("DD_MM_YY")
  // Format follows BE file name, but appends the cityName behind it
  // https://gitlab.myteksi.net/gophers/go/blob/master/mex/mex-insign/api/download/load_csv.go#L60
  // Sample file name from BE: Customer Review - 15_02_24 - 14_05_24.csv
  const fileName = `Customer Review ${start} - ${end} (${city}).csv`

  const body = {
    startDate: filterState.startDate,
    endDate: filterState.endDate,
    queryNames: ["customer-reviews-download"],
    extraFilters: {
      cityName: city,
      serviceType: filterState.serviceType,
      businessTypeFilter: filterState.businessType,
      merchantIDs: filteredOutlets?.map(outlet => outlet.merchantID) || [],
      reviewStatus: filterState.status,
      fileName,
    },
  }

  const token = storageHelper.getToken() || ""
  if (!token) {
    return false
  }

  const generateReportRes = await mexInsightsHttpRequest
    .POST<RequestCsvReportResponse>({
      endpoint: DownloadCsvEndpoint.requestCsvReportV1,
      headers: {
        [AuthorizationHeader.mexInsights]: token,
      },
      data: body as any,
      cancelToken,
    })
    .toPromise()

  if (!generateReportRes?.data?.ref_id) {
    return false
  }

  const attemptDownload = async (): Promise<Boolean> => {
    const statusRes = await mexInsightsHttpRequest
      .GET<GetCsvReportStatusResponse>({
        endpoint: `${DownloadCsvEndpoint.getCsvStatusV1}/${
          generateReportRes.data!.ref_id
        }`,
        headers: {
          [AuthorizationHeader.mexInsights]: token,
        },
        cancelToken,
      })
      .toPromise()

    if (statusRes.data?.status === "IN_PROGRESS") {
      // retry every two seconds until status is `success` or `failed`
      await new Promise(resolve => setTimeout(resolve, 2000))

      return attemptDownload()
    }

    if (statusRes.data?.status === "SUCCESS" && statusRes.data.s3_url) {
      saveFile(statusRes.data.s3_url)

      return true
    }

    return false
  }

  return attemptDownload()
}
